// src/utils/resetTestData.js
//
// Utilidad de reseteo SEGURO de datos de prueba (clientes y rutas), pensada
// para usarse manualmente desde la consola del navegador, en DOS pasos
// obligatorios:
//
//   1. previewResetTestData()          → SOLO LECTURA. No borra nada.
//   2. resetTestData({ confirm: true }) → Borra únicamente lo detectado.
//
// Diseño clave (ver plan acordado):
// - El workspace y los perfiles se descubren SIEMPRE dinámicamente
//   (auth.currentUser.uid + collectionGroup), nunca hardcodeados.
// - Se usa collectionGroup('profiles') acotado por rango de documentId()
//   para encontrar TODOS los documentos reales
//   workspaces/{workspaceId}/days/{dayKey}/profiles/{profileId}
//   SIN depender de que el documento padre days/{dayKey} exista (Firestore
//   permite subcolecciones bajo documentos padre nunca materializados).
// - resetTestData() NUNCA reutiliza un preview antiguo: vuelve a ejecutar
//   la misma consulta internamente justo antes de borrar.
// - Si la consulta interna de resetTestData devuelve 0 documentos de perfil,
//   se ABORTA por defecto (no se borra ni templates ni localStorage), salvo
//   que se indique explícitamente allowEmptyProfiles: true.
// - NUNCA se borra el documento padre days/{dayKey}.
// - Se conserva intacto: documento raíz del workspace, settings (profiles,
//   routeStatus, alerts), autenticación, Billing/trial/suscripciones.

import { db, auth } from "@/firebase";
import {
  collection,
  collectionGroup,
  query,
  where,
  documentId,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import { LOCAL_STORAGE_KEYS } from "@/config/constants";

/** Escapa caracteres especiales de regex en un string (defensivo). */
function escapeRegExp(str) {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Ejecuta la consulta collectionGroup('profiles') acotada por rango de
 * documentId(), y aplica un filtro defensivo adicional por forma exacta
 * de ruta: workspaces/{workspaceId}/days/{dayKey}/profiles/{profileId}
 *
 * IMPORTANTE sobre los límites de documentId() en collectionGroup:
 * Firestore exige que cada límite sea una referencia de DOCUMENTO válida,
 * es decir, una ruta con un número PAR de segmentos (las rutas de
 * documento siempre alternan colección/documento/colección/documento...).
 *
 * El prefijo "workspaces/{workspaceId}/days/" tiene 3 segmentos (impar) —
 * termina en una colección, no en un documento — por eso Firestore lo
 * rechazaba con "odd number of segments".
 *
 * Límite correcto: "workspaces/{workspaceId}" (2 segmentos, par) es una
 * referencia de documento válida (el propio documento del workspace).
 * Usarlo como límite de rango para el mismo patrón oficial de Firebase
 * (ancestro con número par de segmentos) escopa la collectionGroup a
 * todo lo que cuelgue de ese workspace. Como collectionGroup(db,
 * "profiles") ya restringe el ID de colección a "profiles", y la única
 * colección llamada "profiles" en este ancestro es
 * days/{dayKey}/profiles/{profileId}, el resultado ya viene acotado al
 * workspace correcto; el filtro defensivo por regex exacta refuerza esto
 * exigiendo también la forma completa days/{dayKey}/profiles/{profileId}.
 *
 * Esta consulta encuentra documentos de perfil reales AUNQUE el documento
 * padre days/{dayKey} nunca haya sido creado explícitamente con setDoc.
 */
async function findRealProfileDocs(workspaceId) {
  const workspaceDocPath = `workspaces/${workspaceId}`; // 2 segmentos: par, válido
  const q = query(
    collectionGroup(db, "profiles"),
    where(documentId(), ">=", workspaceDocPath),
    where(documentId(), "<", workspaceDocPath + "\uf8ff")
  );

  const snap = await getDocs(q);

  // Filtro defensivo: la ruta debe tener EXACTAMENTE la forma
  // workspaces/{workspaceId}/days/{dayKey}/profiles/{profileId}
  // (6 segmentos, "profiles" como penúltimo segmento de colección).
  const pathPattern = new RegExp(
    `^workspaces/${escapeRegExp(workspaceId)}/days/[^/]+/profiles/[^/]+$`
  );

  return snap.docs.filter((d) => pathPattern.test(d.ref.path));
}


/**
 * Lee los documentos de plantilla (templates) del workspace.
 * A diferencia de "profiles", esta es una colección directa bajo un
 * documento padre (workspaces/{workspaceId}) que siempre existe, por lo
 * que no tiene el problema de "padre fantasma".
 */
async function findTemplateDocs(workspaceId) {
  const templatesRef = collection(db, "workspaces", workspaceId, "templates");
  const snap = await getDocs(templatesRef);
  return snap.docs;
}

/**
 * Extrae { dayKey, profileId } de un path con forma
 * workspaces/{workspaceId}/days/{dayKey}/profiles/{profileId}
 */
function parseProfileDocPath(path) {
  const parts = path.split("/");
  // ["workspaces", ws, "days", dayKey, "profiles", profileId]
  return { dayKey: parts[3], profileId: parts[5] };
}

/**
 * PASO 1 — SOLO LECTURA. Muestra cuántos documentos se borrarían y una
 * muestra de rutas completas para poder verificar antes de confirmar nada.
 * NO borra absolutamente nada.
 */
export async function previewResetTestData() {
  const user = auth.currentUser;
  if (!user) {
    console.error("[RESET_PREVIEW] ❌ No hay usuario autenticado. Abortando preview.");
    return { ready: false, reason: "no_auth" };
  }

  const workspaceId = user.uid;
  console.log(`[RESET_PREVIEW] Analizando workspace: ${workspaceId} (solo lectura, no se borra nada)`);

  const profileDocs = await findRealProfileDocs(workspaceId);
  const templateDocs = await findTemplateDocs(workspaceId);

  const dayKeys = new Set();
  const profileNames = new Set();
  profileDocs.forEach((d) => {
    const { dayKey, profileId } = parseProfileDocPath(d.ref.path);
    dayKeys.add(dayKey);
    profileNames.add(profileId);
  });

  const samplePaths = profileDocs.slice(0, 10).map((d) => d.ref.path);
  const templatePaths = templateDocs.map((d) => d.ref.path);

  const summary = {
    ready: true,
    workspaceId,
    profileDocsCount: profileDocs.length,
    dayKeysAffectedCount: dayKeys.size,
    dayKeysAffected: Array.from(dayKeys).sort(),
    profileNamesFound: Array.from(profileNames).sort(),
    samplePaths,
    templateCount: templateDocs.length,
    templatePaths,
  };

  console.log("[RESET_PREVIEW] ================ RESUMEN (solo lectura) ================");
  console.log(`[RESET_PREVIEW] workspaceId: ${summary.workspaceId}`);
  console.log(`[RESET_PREVIEW] Documentos de perfil reales encontrados (days/*/profiles/*): ${summary.profileDocsCount}`);
  console.log(`[RESET_PREVIEW] Días afectados (${summary.dayKeysAffectedCount}):`, summary.dayKeysAffected);
  console.log(`[RESET_PREVIEW] Perfiles encontrados:`, summary.profileNamesFound);
  console.log(`[RESET_PREVIEW] Muestra de rutas completas (máx. 10) — verifica que apuntan exactamente a`);
  console.log(`[RESET_PREVIEW]   workspaces/{workspaceId}/days/{dayKey}/profiles/{profileId}:`);
  samplePaths.forEach((p) => console.log(`  - ${p}`));
  console.log(`[RESET_PREVIEW] Documentos de template encontrados: ${summary.templateCount}`);
  templatePaths.forEach((p) => console.log(`  - ${p}`));
  console.log("[RESET_PREVIEW] =========================================================");
  console.log("[RESET_PREVIEW] Esto es SOLO UNA VISTA PREVIA. NO se ha borrado nada.");
  console.log("[RESET_PREVIEW] Para borrar (tras revisar lo anterior), ejecuta: resetTestData({ confirm: true })");

  return summary;
}

/**
 * Limpia SOLO las claves de localStorage relacionadas con datos de perro
 * (whitelist explícita). Respeta el sufijo por-usuario que añade
 * src/lib/storage.js (`${key}_${uid}`).
 *
 * NO toca: APP_CONFIG (perfiles, whatsapp, nombres), SELECTED_USER,
 * CURRENT_DATE, UI_THEME, APP_SETTINGS, LAST_ACTIVE_DATE, ni claves
 * `firebase:` (sesión de autenticación).
 */
function cleanTestDataLocalStorage() {
  const exactBaseKeys = [
    LOCAL_STORAGE_KEYS.DAYS_LIST,
    LOCAL_STORAGE_KEYS.COMPLETED_DOGS,
    LOCAL_STORAGE_KEYS.URGENT_DOGS,
    LOCAL_STORAGE_KEYS.PERMANENTLY_DELETED_DOGS,
  ];
  const historyPrefix = LOCAL_STORAGE_KEYS.HISTORY_STATE_PREFIX;

  const removed = [];
  Object.keys(localStorage).forEach((key) => {
    const matchesExactBase = exactBaseKeys.some(
      (base) => key === base || key.startsWith(`${base}_`)
    );
    const matchesHistoryPrefix = key.startsWith(historyPrefix);
    if (matchesExactBase || matchesHistoryPrefix) {
      localStorage.removeItem(key);
      removed.push(key);
    }
  });
  return removed;
}

/**
 * PASO 2 — Ejecuta el borrado real. Requiere { confirm: true } explícito.
 *
 * Seguridad adicional obligatoria:
 * - Vuelve a ejecutar internamente la MISMA consulta collectionGroup justo
 *   antes de borrar (nunca reutiliza el resultado de un preview antiguo).
 * - Si esa consulta interna devuelve 0 documentos de perfil, ABORTA por
 *   defecto sin tocar templates ni localStorage (podría indicar workspace
 *   equivocado, problema de permisos o error de consulta). Solo continúa
 *   si se pasa explícitamente { allowEmptyProfiles: true }, para el caso
 *   legítimo de un workspace que ya está vacío.
 * - Nunca borra el documento padre days/{dayKey}.
 *
 * @param {{confirm?: boolean, allowEmptyProfiles?: boolean}} options
 */
export async function resetTestData({ confirm, allowEmptyProfiles = false } = {}) {
  if (confirm !== true) {
    console.warn(
      "[RESET_TEST_DATA] No se ha borrado nada. Debes llamar primero a previewResetTestData() " +
      "para revisar qué se borraría, y luego a resetTestData({ confirm: true }) para ejecutar el borrado."
    );
    return { executed: false, reason: "not_confirmed" };
  }

  const user = auth.currentUser;
  if (!user) {
    console.error("[RESET_TEST_DATA] ❌ No hay usuario autenticado. Abortando.");
    return { executed: false, reason: "no_auth" };
  }
  const workspaceId = user.uid;

  console.log(
    "[RESET_TEST_DATA] Re-ejecutando la consulta collectionGroup EN TIEMPO REAL " +
    "(nunca se reutiliza un preview antiguo)..."
  );
  const profileDocs = await findRealProfileDocs(workspaceId);

  if (profileDocs.length === 0 && !allowEmptyProfiles) {
    console.error("[RESET_TEST_DATA] ❌ ABORTADO POR SEGURIDAD.");
    console.error(
      "[RESET_TEST_DATA] La consulta no encontró ningún documento real de perfil " +
      "(workspaces/{workspaceId}/days/{dayKey}/profiles/{profileId})."
    );
    console.error(
      "[RESET_TEST_DATA] Si hay datos visibles en la app ahora mismo, NO continúes: " +
      "puede indicar workspace incorrecto, un problema de permisos, o un fallo en la consulta."
    );
    console.error(
      "[RESET_TEST_DATA] Si estás seguro de que el workspace ya está vacío, ejecuta: " +
      "resetTestData({ confirm: true, allowEmptyProfiles: true })"
    );
    console.error("[RESET_TEST_DATA] NO se ha borrado ningún dato (ni templates ni localStorage).");
    return { executed: false, reason: "zero_profile_docs_safety_abort" };
  }

  console.log(`[RESET_TEST_DATA] Borrando ${profileDocs.length} documento(s) de perfil real(es)...`);
  let deletedProfiles = 0;
  const failedProfiles = [];
  for (const docSnap of profileDocs) {
    try {
      await deleteDoc(docSnap.ref);
      deletedProfiles++;
      console.log(`  ✓ Eliminado: ${docSnap.ref.path}`);
    } catch (e) {
      failedProfiles.push(docSnap.ref.path);
      console.error(`  ✗ FALLO eliminando ${docSnap.ref.path}:`, e.message);
    }
  }

  console.log("[RESET_TEST_DATA] Borrando documentos de templates...");
  const templateDocs = await findTemplateDocs(workspaceId);
  let deletedTemplates = 0;
  const failedTemplates = [];
  for (const docSnap of templateDocs) {
    try {
      await deleteDoc(docSnap.ref);
      deletedTemplates++;
      console.log(`  ✓ Eliminado: ${docSnap.ref.path}`);
    } catch (e) {
      failedTemplates.push(docSnap.ref.path);
      console.error(`  ✗ FALLO eliminando ${docSnap.ref.path}:`, e.message);
    }
  }

  console.log("[RESET_TEST_DATA] Limpiando localStorage (solo claves de datos de perro)...");
  const cleanedKeys = cleanTestDataLocalStorage();

  console.log("[RESET_TEST_DATA] ✅ COMPLETADO");
  console.log(`  - Documentos de perfil eliminados: ${deletedProfiles}/${profileDocs.length}`);
  if (failedProfiles.length > 0) {
    console.warn(`  - Documentos de perfil que fallaron: ${failedProfiles.length}`, failedProfiles);
  }
  console.log(`  - Documentos de template eliminados: ${deletedTemplates}/${templateDocs.length}`);
  if (failedTemplates.length > 0) {
    console.warn(`  - Documentos de template que fallaron: ${failedTemplates.length}`, failedTemplates);
  }
  console.log(`  - Claves de localStorage eliminadas (${cleanedKeys.length}):`, cleanedKeys);
  console.log("  - NO se ha tocado ningún documento padre days/{dayKey}.");
  console.log("  - NO se ha tocado: workspace raíz, settings (profiles/routeStatus/alerts), autenticación, Billing/trial/suscripciones.");
  console.log("  - Recarga la página para ver la aplicación limpia.");

  return {
    executed: true,
    deletedProfiles,
    failedProfiles,
    deletedTemplates,
    failedTemplates,
    cleanedLocalStorageKeys: cleanedKeys,
  };
}

// Exposición manual desde la consola del navegador (mismo patrón que
// resetApp/hardReset/deepCleanup en src/utils/*).
if (typeof window !== "undefined") {
  window.previewResetTestData = previewResetTestData;
  window.resetTestData = resetTestData;
  console.log(
    "💡 Tip: Ejecuta previewResetTestData() para revisar qué se borraría, " +
    "y luego resetTestData({ confirm: true }) para borrar realmente."
  );
}
